import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { ArrowRight, Store, Building2, Network } from "lucide-react";
import { Link } from "react-router-dom";
import { useRegion } from "../../../../regions/RegionContext";

// Background: AI-generated Indian restaurant scene (Higgsfield / Seedance,
// owned by us) — copper handi curry, naan and biryani with a tandoor chef and
// diners behind. ~1MB, muted, self-hosted in public/media.
const HERO_VIDEO = "/media/hero-restaurant.mp4";
const HERO_POSTER = "/media/hero-restaurant.jpg";

// Who we serve — mirrors the audience strip in the reference design, phrased
// for the journey in the tagline: start → systemize → scale.
const audiences = [
  { icon: Store, label: "Independent Restaurants" },
  { icon: Building2, label: "Multi Outlet Chains" },
  { icon: Network, label: "Franchise Outlets" },
];

// Rotating tail of the caption — swaps every 5 seconds.
const PHRASES = ["Right Decisions", "Better Systems", "Practical Solutions"];

const HeroSection = () => {
  const { config } = useRegion();
  const heroTagline = config.content.heroTagline;
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setPhraseIndex((prev) => (prev + 1) % PHRASES.length),
      5000
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "640px" }}
    >
      {/* Background video */}
      <video
        className="absolute inset-0 z-0 w-full h-full object-cover"
        src={HERO_VIDEO}
        poster={HERO_POSTER}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />

      {/* Overlay — dark wash, stronger on the left where the copy sits */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/80 via-black/55 to-black/25 pointer-events-none" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/50 via-transparent to-black/30 pointer-events-none" />

      {/* Content — vertically centered; left-aligned on tablet/desktop,
          centered on mobile (max-md) */}
      <div className="relative z-20 h-full flex items-center">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-10 md:px-16">
          <div
            className="max-w-[900px] flex flex-col items-start text-left max-md:items-center max-md:text-center"
            // Nudges the whole text block down, widening the gap between the
            // header and the tag by ~15% (pure visual shift, layout untouched)
            style={{ transform: "translateY(1.5svh)" }}
          >
            {/* Tag */}
            <motion.span
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block text-[11px] sm:text-xs md:text-sm font-bold text-[#4ade80] uppercase tracking-widest"
              // 15% above the default 1rem (mb-4) gap to the caption
              style={{ marginBottom: "1.15rem" }}
            >
              Data-Driven Restaurant Consulting
            </motion.span>

            {/* Caption */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-extrabold text-white mb-5"
              style={{
                // 15% above the previous clamp(1.62rem, 3.91vw, 3.06rem)
                fontSize: "clamp(1.86rem, 4.5vw, 3.52rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.01em",
              }}
            >
              Building Profitable Restaurants With{" "}
              {/* leading-[inherit] neutralizes the global `span { line-height:
                  1.7 }` (index.css) that would otherwise inflate the line
                  boxes this span sits on, leaving an uneven gap mid-heading */}
              <span className="inline-block whitespace-nowrap">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={phraseIndex}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-block text-[#4ade80] leading-[inherit]"
                  >
                    {PHRASES[phraseIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.h1>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-white/85 leading-relaxed mb-8"
              style={{ fontSize: "clamp(0.95rem, 1.6vw, 1.15rem)", maxWidth: "640px" }}
            >
              From Small Cafes To Expanding Restaurant Brands, We Help You
              Start, Systemize And Scale With Confidence.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap items-center max-md:justify-center gap-3 mb-10"
            >
              <Link to="/contact" className="inline-block shrink-0">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 400, damping: 18 }}
                  className="group relative px-6 sm:px-8 py-3 sm:py-3.5 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-sm sm:text-base shadow-lg flex items-center gap-2 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Book an Audit
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                </motion.button>
              </Link>

              <Link to="/services" className="inline-block shrink-0">
                <motion.button
                  whileHover={{ scale: 1.04, borderColor: "rgba(255,255,255,0.7)" }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 400, damping: 18 }}
                  className="px-6 sm:px-8 py-3 sm:py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/40 rounded-full font-semibold text-sm sm:text-base backdrop-blur-sm transition-colors duration-300"
                >
                  View Services
                </motion.button>
              </Link>
            </motion.div>

            {/* Audience strip — bordered box like the reference design.
                Stacks as a column below lg (centered items on mobile,
                left-aligned on tablet), row on desktop. */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="inline-flex flex-col items-start max-md:items-center lg:flex-row lg:flex-wrap lg:items-center gap-x-8 gap-y-3 border border-white/25 rounded-xl bg-black/25 backdrop-blur-sm px-6 py-4"
            >
              {audiences.map(({ icon: AudienceIcon, label }) => (
                <div key={label} className="flex items-center gap-2.5">
                  <AudienceIcon
                    className="w-5 h-5 shrink-0 text-white/80"
                    strokeWidth={1.8}
                  />
                  <span className="text-sm sm:text-[15px] font-medium text-white leading-normal">
                    {label}
                  </span>
                </div>
              ))}
            </motion.div>

            {/* Region tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="text-xs text-white/45 mt-6 hidden lg:block"
            >
              {heroTagline}
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
